import React from 'react';
import { Button, Checkbox, FormError, FormInput, Message } from '@getro/rombo';
import { Box, Flex, Text } from 'rebass/styled-components';
import { Formik, Form, Field } from 'formik';
import { useHistory, useParams } from 'react-router-dom';
import * as Yup from 'yup';
import PropTypes from 'prop-types';

import { FormImage } from '../../../components/molecules/formImage';
import { trackEvent } from '../../../helpers/analytics';
import { useUpdateUserMutation } from '../../../services/user';
import { legalDocumentsVersion } from '../../../settings/legalDocumentsVersion';
import UserAndCompanyProfile from './UserAndCompanyProfile';

export const Profile = ({ user, network }) => {
  const [updateUser, { isLoading, isError }] = useUpdateUserMutation();
  const { mode } = useParams();
  const history = useHistory();

  const formSchema = Yup.object().shape({
    firstName: Yup.string().required(),
    lastName: Yup.string().required(),
    ...(mode === 'email' ? { email: Yup.string().email().required() } : undefined),
    ...(mode === 'email' ? { password: Yup.string().required() } : undefined),
    agree: Yup.boolean().oneOf([true], 'You must agree to the terms of service and privacy policy'),
  });

  return (
    <Flex sx={{ flexDirection: 'column', gap: '40px' }}>
      <Flex sx={{ flexDirection: 'column', gap: '16px', flex: 1 }}>
        {network && user && <UserAndCompanyProfile network={network} user={user} />}
        <Text sx={{ fontWeight: 600, fontSize: '22px', color: 'text.dark', lineHeight: '1.4' }}>
          Start collaborating with {network.name}
        </Text>
        <Text>
          Share your contacts with them on Getro, so they can find high-impact introductions between them and their
          network.
        </Text>
      </Flex>
      <Box>
        <Formik
          initialValues={{
            avatarUrl: user.avatarUrl || '',
            ...(mode === 'email' ? { email: user.email } : undefined),
            ...(mode === 'email' ? { password: '' } : undefined),
            agree: false,
            firstName: user.firstName,
            lastName: user.lastName,
          }}
          validationSchema={formSchema}
          enableReinitialize={false}
          onSubmit={async (data) => {
            const res = await updateUser({
              password: data.password,
              passwordConfirmation: data.password,
              currentPassword: data.password,
              email: data.email,
              onboardingCompleted: true,
              privacyPolicyVersion: legalDocumentsVersion.privacyPolicyVersion,
              termsPolicyVersion: legalDocumentsVersion.termsPolicyVersion,
              firstName: data.firstName,
              lastName: data.lastName,
            });

            if (res.data) {
              trackEvent('ally_onboarding:account_created', {
                collection_id: network?.id,
              });
              history.push(`/onboarding/${network.slug}/linkedin/sync?ally=true`);
            }
          }}
        >
          {({ values, setFieldValue, errors }) => (
            <Box as={Form} sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <Flex mb="8px" alignItems="center" sx={{ gap: '16px' }}>
                <Field
                  name="avatarUrl"
                  component={FormImage}
                  imagePlaceholder={`${values.firstName} ${values.lastName}`}
                />
              </Flex>
              <Field
                component={FormInput}
                data-cy="login-first-name"
                name="firstName"
                label="First name"
                size="medium"
                value={values.firstName}
              />
              <Field
                component={FormInput}
                data-cy="login-last-name"
                name="lastName"
                label="Last name"
                size="medium"
                value={values.lastName}
              />
              {mode === 'email' && (
                <>
                  <Field
                    component={FormInput}
                    data-cy="login-email"
                    type="email"
                    name="email"
                    label="Email address"
                    placeholder="Enter your email address"
                    size="medium"
                    value={values.email}
                    disabled
                  />
                  <Field
                    component={FormInput}
                    data-cy="login-password"
                    type="password"
                    name="password"
                    label="Password"
                    placeholder="Create a new password"
                    value={values.password}
                    size="medium"
                  />
                </>
              )}
              <Flex flexDirection="column" sx={{ gap: '8px' }}>
                <Flex>
                  <Checkbox checked={values.agree} onChange={() => setFieldValue('agree', !values.agree)} />
                  <Text
                    sx={{ cursor: 'pointer' }}
                    fontSize="14px"
                    onClick={() => setFieldValue('agree', !values.agree)}
                  >
                    I agree to the{' '}
                    <Box
                      as="a"
                      href="https://www.getro.com/terms"
                      onClick={(e) => e.stopPropagation()}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Terms of Service
                    </Box>{' '}
                    and{' '}
                    <Box
                      as="a"
                      href="https://www.getro.com/privacy"
                      onClick={(e) => e.stopPropagation()}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Privacy Policy
                    </Box>
                  </Text>
                </Flex>
                {errors.agree && <FormError m="0px">{errors.agree}</FormError>}
              </Flex>
              <Button
                fontWeight="semibold"
                width="100%"
                justifyContent="center"
                type="submit"
                variant="primary"
                loading={isLoading}
                size="medium"
              >
                Create account
              </Button>
              {isError && (
                <Message
                  type="error"
                  className="Login__error-message"
                  content="There was an unexpected error, please try again or contact help@getro.com."
                />
              )}
            </Box>
          )}
        </Formik>
      </Box>
    </Flex>
  );
};

Profile.propTypes = {
  user: PropTypes.object.isRequired,
  network: PropTypes.object.isRequired,
};
